import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useCartStore } from "@/stores/cart";
import { formatFCFA } from "@cieda/utils";

const STALENESS_CHECK_KEY = "djayma-cart-staleness-checked";
const STALENESS_INTERVAL_MS = 5 * 60 * 1000; // Re-check every 5 minutes on focus

/**
 * Validates cart item prices and stock against the database.
 * Runs on mount AND when the tab regains focus (debounced to every 5 min).
 *
 * - Price changed → updates snapshot, toasts the user
 * - Out of stock → removes item, toasts
 * - Stock reduced below quantity → clamps quantity, toasts
 */
export function useCartStaleness() {
  const items = useCartStore((s) => s.items);
  const _replaceItems = useCartStore((s) => s._replaceItems);
  const _getPersistedItems = useCartStore((s) => s._getPersistedItems);
  const hasRun = useRef(false);
  const lastCheckRef = useRef(0);

  useEffect(() => {
    // Skip initial run if already checked this session
    const alreadyChecked = sessionStorage.getItem(STALENESS_CHECK_KEY);
    if (hasRun.current && alreadyChecked) return;
    if (items.length === 0) return;

    hasRun.current = true;

    const checkStaleness = async () => {
      // Debounce: skip if checked less than 5 minutes ago
      if (Date.now() - lastCheckRef.current < STALENESS_INTERVAL_MS) return;
      lastCheckRef.current = Date.now();
      const persisted = _getPersistedItems();
      const productIds = persisted.map((i) => i.snapshot.id);

      const { data: freshProducts, error } = await supabase
        .from("products")
        .select("id, name, price_fcfa, stock, weight_kg, tva_applicable, tva_rate, min_order_qty, image_url, brand, unit")
        .in("id", productIds);

      if (error || !freshProducts) {
        // Don't block the user if the check fails — just skip
        sessionStorage.setItem(STALENESS_CHECK_KEY, "1");
        return;
      }

      const freshMap = new Map(freshProducts.map((p) => [p.id, p]));
      const changes: string[] = [];
      let hasChanges = false;

      const updatedItems = persisted
        .map((item) => {
          const fresh = freshMap.get(item.snapshot.id);

          // Product no longer exists or deactivated
          if (!fresh) {
            changes.push(`${item.snapshot.name} n'est plus disponible`);
            hasChanges = true;
            return null;
          }

          // Out of stock
          if (fresh.stock <= 0) {
            changes.push(`${fresh.name} est en rupture de stock`);
            hasChanges = true;
            return null;
          }

          let newQuantity = item.quantity;
          const updatedSnapshot = { ...item.snapshot };

          // Price changed
          if (fresh.price_fcfa !== item.snapshot.price_fcfa) {
            changes.push(
              `Prix de ${fresh.name} : ${formatFCFA(item.snapshot.price_fcfa)} → ${formatFCFA(fresh.price_fcfa)}`,
            );
            updatedSnapshot.price_fcfa = fresh.price_fcfa;
            hasChanges = true;
          }

          // Stock reduced below current quantity
          if (fresh.stock < item.quantity) {
            changes.push(
              `Stock limité pour ${fresh.name} : quantité ajustée à ${fresh.stock}`,
            );
            newQuantity = fresh.stock;
            hasChanges = true;
          }

          // Update snapshot with fresh data silently
          updatedSnapshot.stock = fresh.stock;
          updatedSnapshot.weight_kg = fresh.weight_kg ?? item.snapshot.weight_kg;
          updatedSnapshot.tva_applicable = fresh.tva_applicable ?? item.snapshot.tva_applicable;
          updatedSnapshot.tva_rate = fresh.tva_rate ?? item.snapshot.tva_rate;
          updatedSnapshot.min_order_qty = fresh.min_order_qty ?? item.snapshot.min_order_qty;
          updatedSnapshot.image_url = fresh.image_url ?? item.snapshot.image_url;
          updatedSnapshot.brand = fresh.brand ?? item.snapshot.brand;
          updatedSnapshot.unit = fresh.unit ?? item.snapshot.unit;

          return { snapshot: updatedSnapshot, quantity: newQuantity };
        })
        .filter(Boolean) as Array<{ snapshot: typeof persisted[0]["snapshot"]; quantity: number }>;

      if (hasChanges) {
        _replaceItems(updatedItems);

        // Count removed products (404 or out of stock)
        const removedCount = persisted.length - updatedItems.length;
        if (removedCount > 0) {
          toast.warning("Panier mis à jour", {
            description:
              removedCount === 1
                ? "1 produit n'est plus disponible et a été retiré de votre panier."
                : `${removedCount} produits ne sont plus disponibles et ont été retirés de votre panier.`,
          });
        }

        // Toast price/stock changes (max 3, then summary)
        const priceStockChanges = changes.filter(
          (msg) => !msg.includes("n'est plus disponible") && !msg.includes("rupture de stock"),
        );
        const toShow = priceStockChanges.slice(0, 3);
        const remaining = priceStockChanges.length - toShow.length;

        for (const msg of toShow) {
          toast.info("Panier mis à jour", { description: msg });
        }
        if (remaining > 0) {
          toast.info("Panier mis à jour", {
            description: `Et ${remaining} autre(s) modification(s).`,
          });
        }
      }

      sessionStorage.setItem(STALENESS_CHECK_KEY, "1");
    };

    checkStaleness();

    // Also recheck when tab regains focus (catches price changes during long sessions)
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkStaleness();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [items.length, _replaceItems, _getPersistedItems]);
}
